import PropTypes from 'prop-types';
import React from 'react';
import keycode from 'keycode';
import ReactSelect from 'react-select-v1';
import styled, { css } from 'styled-components';
import { colors, elevation, fonts } from 'bench-style-constants';

import { Fill } from 'layout-core-components';
import { Label, Icon, Text } from 'primitive-components';
import FloatingActions from 'floating-actions';
import { fadeIn, slideAndFadeInLeft } from './keyframes';

/**
 * @typedef {Object} ReactSelectOption
 * @property {string} label
 * @property {*} value
 */

const CategoryLabel = styled(Label)`
  color: ${colors.dust};
  letter-spacing: 1.3px;
  padding-bottom: 8px;
`;

const StyledSegment = styled.div`
  cursor: pointer;
`;

const animationRule = css`
  ${slideAndFadeInLeft('16px')} 0.2s ease-out forwards;
`;

const StyledSegmentInput = styled.div`
  cursor: text;
  border-radius: 0.25rem;

  .Select-control,
  .Select.is-focused > .SelectControl,
  .Select.is-focused:not(.is-open) > .SelectControl {
    background: ${colors.pearl} !important;
    border: none;
    height: 40px;
  }

  .Select-placeholder,
  .Select-value {
    animation: ${animationRule};
    line-height: 40px;
  }

  .Select-value-label {
    line-height: 40px;
  }

  .Select-menu-outer {
    animation: ${fadeIn} 0.2s ease-out forwards;
    border: none;
    border-radius: 4px;
    box-shadow: ${elevation.level1};
    margin-top: 4px;
  }

  .Select-menu-outer .Select-option.is-focused {
    color: ${colors.water};
    background: ${colors.brook};
  }

  .Select-arrow-zone {
    display: none;
  }
`;

const StyledText = styled(Text)`
  padding: 8px 0;
`;

const StyleCreateButton = styled.button`
  align-items: normal;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  font-family: ${fonts.circular};
  font-size: 16px;
  justify-content: space-between;
  line-height: 24px;
  padding: 8px 0;
  width: 100%;
`;

const StyledPlusIcon = styled(Icon)`
  fill: ${colors.water};
  height: 24px;
  width: 24px;
`;

const StyledLabel = styled.span`
  color: ${colors.water};
  display: inline-block;
  text-align: left;
`;

/**
 * @callback handleSegmentInputChange
 * @param {string} clientSegmentId
 * @param {string} dataEntrySegmentId
 * @return {Promise}
 */

/**
 * @typedef {Object<string, any>} SegmentInputProps
 * @property {string} categoryName
 * @property {handleSegmentInputChange} onChange Change handler
 * @property {function(dataEntrySegmentId: string): Promise} onDelete Delete handler
 * @property {ReactSelectOption[]} options
 * @property {ReactSelectOption} selectedOption
 */

/**
 * @typedef {Object<string, any>} SegmentInputState
 * @property {boolean} editMode Whether the user is currently editing
 * @property {ReactSelectOption} selectedOption the dataEntrySegment currently saved in the database
 */

/**
 * @class
 * @property {SegmentInputProps} props
 * @property {SegmentInputState} state
 */
class SegmentInput extends React.Component {
  constructor({ selectedOption }) {
    super();
    this.state = {
      editMode: false,
      selectedOption,
    };
  }

  cancelInput() {
    this.exitEditMode();
  }

  enterEditMode(event) {
    event.stopPropagation();
    this.setState({ editMode: true });
  }

  exitEditMode() {
    this.setState({ editMode: false });
  }

  handleSelectChange(selected) {
    this.setState({ selectedOption: selected });
  }

  delete(selected) {
    this.setState({ selectedOption: null });
    this.props.onDelete(selected.value);
  }

  save() {
    this.exitEditMode();

    if (this.state.selectedOption) {
      this.props.onChange(this.state.selectedOption.value);
    }
  }

  render() {
    const { categoryName, options } = this.props;
    const { editMode, selectedOption } = this.state;

    if (!(selectedOption || editMode)) {
      return (
        <div>
          <CategoryLabel>{categoryName}</CategoryLabel>
          <StyleCreateButton onClick={e => this.enterEditMode(e)}>
            <StyledLabel>{`Add a ${categoryName}`}</StyledLabel>
            <StyledPlusIcon icon="plus" />
          </StyleCreateButton>
        </div>
      );
    }

    return (
      <div>
        <CategoryLabel>{categoryName}</CategoryLabel>
        <div>
          {editMode ? (
            <StyledSegmentInput
              onKeyDown={e => {
                const key = keycode(e);
                if (e.target.classList.contains('remove-button')) return;
                if (key === 'enter') {
                  this.save();
                }
                if (key === 'esc') {
                  this.cancelInput();
                }
              }}
              onBlur={e => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                this.save();
              }}
            >
              <FloatingActions>
                <Fill width="parent">
                  <ReactSelect
                    autoFocus
                    openOnFocus
                    clearable={false}
                    searchable
                    name="segmentID"
                    options={
                      selectedOption
                        ? options.filter(option => option.value !== selectedOption.value)
                        : options
                    }
                    onChange={selected => this.handleSelectChange(selected)}
                    value={selectedOption}
                  />
                </Fill>
                <FloatingActions.List transition padRight>
                  <FloatingActions.Item
                    icon="checkmark"
                    onClick={() => this.save()}
                    styleType="primary"
                  />
                  <FloatingActions.Item
                    icon="cancel"
                    onClick={() => this.exitEditMode()}
                    styleType="danger"
                  />
                </FloatingActions.List>
              </FloatingActions>
            </StyledSegmentInput>
          ) : (
            <StyledSegment>
              <FloatingActions>
                <Fill width="parent">
                  <StyledText onClick={ev => this.enterEditMode(ev, true)}>
                    {selectedOption ? selectedOption.label : 'Choose a Category'}
                  </StyledText>
                </Fill>
                <FloatingActions.List transition>
                  <FloatingActions.Item
                    icon="pencil"
                    onClick={ev => this.enterEditMode(ev, true)}
                  />
                  <FloatingActions.Item
                    styleType="danger"
                    icon="trash"
                    onClick={() => this.delete(this.state.selectedOption)}
                  />
                </FloatingActions.List>
              </FloatingActions>
            </StyledSegment>
          )}
        </div>
      </div>
    );
  }
}

SegmentInput.propTypes = {
  categoryName: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedOption: PropTypes.object, // eslint-disable-line
};

SegmentInput.defaultProps = {
  selectedOption: null,
};

export default SegmentInput;
